import type { FormaRent } from '../core/FormaRent';
import { parseHash } from '../utils/parseHash';

const HASH_PREFIX = '#modal:forma-rent';

export function installHashLinks(instance: FormaRent): () => void {
  const tryOpenFromHash = (hash: string): boolean => {
    const params = parseHash(hash);
    if (params === null) return false;
    instance.open(params);
    return true;
  };

  const clickHandler = (e: MouseEvent): void => {
    // Не перехватываем клики которые юзер хочет открыть в новой вкладке/окне
    // (Ctrl/Cmd/Shift/Alt + click, средняя кнопка) и уже отменённые другими хендлерами.
    if (e.defaultPrevented) return;
    if (e.button > 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const el = e.target as HTMLElement | null;
    const link = el?.closest?.('a[href*="modal:forma-rent"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute('href') ?? '';
    const hashIdx = href.indexOf('#');
    if (hashIdx === -1) return;
    const hashPart = href.slice(hashIdx);
    const params = parseHash(hashPart);
    if (params === null) return;

    e.preventDefault();

    // Обновляем URL чтобы юзер мог скопировать ссылку из адресной строки и расшарить.
    // pushState не триггерит hashchange — двойного open через hashChangeHandler не будет.
    if (window.location.hash !== hashPart) {
      try {
        history.pushState(null, '', hashPart);
      } catch {
        /* sandboxed контексты могут блокировать pushState — open всё равно сработает */
      }
    }

    instance.open(params);
  };
  document.addEventListener('click', clickHandler);

  const hashChangeHandler = (): void => {
    if (window.location.hash.startsWith(HASH_PREFIX)) {
      tryOpenFromHash(window.location.hash);
    }
  };
  window.addEventListener('hashchange', hashChangeHandler);

  // На close убираем hash чтобы reload не открывал модалку повторно и Back в браузере
  // не возвращал юзера в неё. replaceState — не засоряем историю «закрытыми» состояниями.
  const unsubscribeClose = instance.on('close', () => {
    if (window.location.hash.startsWith(HASH_PREFIX)) {
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch {
        /* sandboxed контексты могут блокировать replaceState — не критично */
      }
    }
  });

  // Auto-open при загрузке если в URL уже есть modal-hash (юзер пришёл по расшаренной ссылке).
  // Откладываем до DOMContentLoaded если body ещё не готов (иначе Modal.mount() упадёт).
  const autoOpenIfNeeded = (): void => {
    if (window.location.hash.startsWith(HASH_PREFIX)) {
      tryOpenFromHash(window.location.hash);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoOpenIfNeeded, { once: true });
  } else {
    autoOpenIfNeeded();
  }

  return (): void => {
    document.removeEventListener('click', clickHandler);
    window.removeEventListener('hashchange', hashChangeHandler);
    unsubscribeClose();
  };
}
